type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

export type InstallPlatform = "prompt" | "ios" | "in-app" | "manual";

const DISMISS_KEY = "bibow.install-dismissed-at";
const DISMISS_DAYS = 30;

function userAgent() {
  return typeof navigator === "undefined" ? "" : navigator.userAgent;
}

/** Messenger, Facebook and Instagram open links in their own browser, which can't install apps. */
function isInAppBrowser() {
  return /FBAN|FBAV|FB_IAB|FBIOS|Messenger|Instagram|Line\//i.test(userAgent());
}

function isIos() {
  const ua = userAgent();
  // iPadOS reports itself as a Mac, so also check for touch.
  return (
    /iPhone|iPad|iPod/i.test(ua) ||
    (/Macintosh/i.test(ua) && typeof navigator !== "undefined" && navigator.maxTouchPoints > 1)
  );
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/**
 * Tracks whether the site can be installed as an app and how. Chrome and
 * Android fire `beforeinstallprompt`, which we keep so our own button can open
 * the install dialog; iPhone Safari never does, so it gets instructions.
 */
class InstallState {
  deferred = $state<InstallPromptEvent | null>(null);
  installed = $state(isStandalone());
  /** Open when the browser can't show its own install dialog and needs instructions. */
  helpOpen = $state(false);

  constructor() {
    if (typeof window === "undefined") return;
    window.addEventListener("beforeinstallprompt", (event) => {
      event.preventDefault();
      this.deferred = event as InstallPromptEvent;
    });
    window.addEventListener("appinstalled", () => {
      this.installed = true;
      this.deferred = null;
    });
  }

  get platform(): InstallPlatform {
    if (this.deferred) return "prompt";
    if (isInAppBrowser()) return "in-app";
    if (isIos()) return "ios";
    return "manual";
  }

  /** Opens the browser's install dialog when available; returns false when instructions are needed. */
  async install() {
    const event = this.deferred;
    if (!event) return false;
    await event.prompt();
    const { outcome } = await event.userChoice;
    this.deferred = null;
    if (outcome === "accepted") this.installed = true;
    return true;
  }

  /** Install directly where possible, otherwise show how to add it to the home screen. */
  async start() {
    if (!(await this.install())) this.helpOpen = true;
  }

  dismissedRecently() {
    try {
      const at = Number(localStorage.getItem(DISMISS_KEY));
      return Number.isFinite(at) && Date.now() - at < DISMISS_DAYS * 86_400_000;
    } catch {
      return false;
    }
  }

  dismiss() {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* storage blocked; the banner simply returns next visit */
    }
  }
}

export const installState = new InstallState();

export function registerServiceWorker() {
  if (
    !import.meta.env.PROD ||
    typeof navigator === "undefined" ||
    !("serviceWorker" in navigator)
  ) {
    return;
  }
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      /* install support is a nice-to-have; the site works without it */
    });
  });
}
